import React, { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Loader2, AlertCircle, FileX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  UploadDocumentsWrapper,
  UploadDocumentsContainer,
  HeaderSection,
  SectionTitle,
  SectionDescription,
  ActionButtons,
  DashboardButton,
  SaveButton,
  ContentGrid,
  DocumentDetailsSection,
  UploadSection,
  SectionLabel,
  FormGroup,
  InputLabel,
  TextInput,
  TextAreaInput,
  CharacterCount,
  ErrorText,
  UploadedDocumentsSection,
  DocumentsList,
  EmptyState,
  GlobalError,
} from './uploadDocuments.styles';

import DropZone from './components/Dropzone';
import DocumentListItem from './components/Documentlistitem';

import { useSaveDocument, useSaveDocumentList } from './hooks/Useuploaddocuments';
import { validateUploadForm, formatFileSize, getUploadedAt } from './utils';

const MAX_DESCRIPTION_LENGTH = 500;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const UploadDocuments = () => {
  const navigate = useNavigate();

  // One folder for the whole session — same as before, no per-file folders.
  const [folderName] = useState(() => uuidv4().replace(/-/g, ''));

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // Each item: { id, file, name, size, status, documentId, uploadedAt, errorMessage }
  // status: 'pending' | 'uploading' | 'done' | 'error' | 'size-error' | 'skipped'
  const [documents, setDocuments] = useState([]);

  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState('');

  // ─── Derived state ──────────────────────────────────────────────────────────

  const isUploadingAny = documents.some((d) => d.status === 'uploading');
  const hasPendingOrUploading = documents.some(
    (d) => d.status === 'pending' || d.status === 'uploading'
  );

  // ─── Mutation ───────────────────────────────────────────────────────────────

  const { mutate: uploadDocument } = useSaveDocument(folderName);

  const { mutate: saveList, isPending: isSaving } = useSaveDocumentList({
    onSuccess: (data) => {
      navigate(`/processing/${data?._id}`);
    },
    onError: (err) => {
      setGlobalError(err?.message || 'Failed to save document list. Please try again.');
    },
  });

  // ─── Sequential upload engine ───────────────────────────────────────────────
  // Uploads exactly one file at a time. Whenever nothing is uploading, pick
  // the next 'pending' doc and start it. Retries just flip a doc back to
  // 'pending' and let this effect pick it up the same way.

  const startUpload = useCallback(
    (doc) => {
      setDocuments((prev) =>
        prev.map((d) => (d.id === doc.id ? { ...d, status: 'uploading', errorMessage: '' } : d))
      );

      uploadDocument(
        { file: doc.file },
        {
          onSuccess: (data) => {
            setDocuments((prev) =>
              prev.map((d) =>
                d.id === doc.id
                  ? {
                      ...d,
                      status: 'done',
                      documentId: data.documentId,
                      uploadedAt: `Uploaded at ${getUploadedAt()}`,
                    }
                  : d
              )
            );
          },
          onError: (err) => {
            setDocuments((prev) =>
              prev.map((d) =>
                d.id === doc.id
                  ? {
                      ...d,
                      status: 'error',
                      errorMessage: err?.message || 'Upload failed. Please retry.',
                    }
                  : d
              )
            );
          },
        }
      );
    },
    [uploadDocument]
  );

  useEffect(() => {
    if (documents.length === 0) return;
    if (isUploadingAny) return; // one at a time — wait for current to settle

    const nextPending = documents.find((d) => d.status === 'pending');
    if (nextPending) startUpload(nextPending);
  }, [documents, isUploadingAny, startUpload]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleFilesSelect = useCallback((fileList) => {
    const fileArray = Array.from(fileList || []);
    if (fileArray.length === 0) return;

    setGlobalError('');
    setErrors((prev) => ({ ...prev, documents: undefined }));

    const queue = fileArray.map((file) => {
      const isSizeExceeded = file.size > MAX_FILE_SIZE;
      return {
        id: uuidv4(),
        file,
        name: file.name,
        size: formatFileSize(file.size),
        status: isSizeExceeded ? 'size-error' : 'pending',
        documentId: null,
        uploadedAt: '',
        errorMessage: isSizeExceeded
          ? 'File exceeds 50MB limit. Please choose a smaller file.'
          : '',
      };
    });

    setDocuments((prev) => [...prev, ...queue]);
  }, []);

  const handleReupload = useCallback((doc) => {
    setGlobalError('');
    setDocuments((prev) =>
      prev.map((d) => (d.id === doc.id ? { ...d, status: 'pending', errorMessage: '' } : d))
    );
  }, []);

  const handleSkip = useCallback((doc) => {
    setGlobalError('');
    setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, status: 'skipped' } : d)));
  }, []);

  const handleSave = () => {
    setGlobalError('');

    const successfulDocs = documents.filter((d) => d.status === 'done');

    const { isValid, errors: validationErrors } = validateUploadForm({
      title,
      documents: successfulDocs,
    });

    const descriptionError = !description.trim() ? 'Description is required' : undefined;
    const mergedErrors = { ...validationErrors, description: descriptionError };
    setErrors(mergedErrors);

    if (!isValid || descriptionError) return;

    if (hasPendingOrUploading) {
      setGlobalError('Please wait for uploads to finish (or skip failed files) before saving.');
      return;
    }

    saveList({
      title: title.trim(),
      folderName,
      pdfArray: successfulDocs.map((d) => ({ documentId: d.documentId })),
      description: description.trim(),
    });
  };

  const handleDashboard = () => navigate('/');

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    if (e.target.value.trim()) setErrors((prev) => ({ ...prev, title: undefined }));
  };

  const handleDescriptionChange = (e) => {
    if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(e.target.value);
      if (e.target.value.trim()) setErrors((prev) => ({ ...prev, description: undefined }));
    }
  };

  const buttonsDisabled = isUploadingAny;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <UploadDocumentsWrapper>
      <UploadDocumentsContainer>
        <HeaderSection>
          <div>
            <SectionTitle>Upload Documents</SectionTitle>
            <SectionDescription>
              Add documents to process and extract information
            </SectionDescription>
          </div>
          <ActionButtons>
            <DashboardButton onClick={handleDashboard} disabled={buttonsDisabled} type="button">
              Dashboard
            </DashboardButton>
            <SaveButton onClick={handleSave} disabled={isSaving || buttonsDisabled} type="button">
              {isSaving ? (
                <Loader2
                  size={14}
                  className="spin"
                  style={{ animation: 'spin 1s linear infinite' }}
                />
              ) : null}
              {isSaving ? 'Saving…' : 'Save'}
            </SaveButton>
          </ActionButtons>
        </HeaderSection>

        {globalError && (
          <GlobalError role="alert">
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            {globalError}
          </GlobalError>
        )}

        <ContentGrid>
          <DocumentDetailsSection>
            <SectionLabel>Document Details</SectionLabel>

            <FormGroup>
              <InputLabel htmlFor="doc-title">
                Title{' '}
                <span style={{ color: 'var(--color-error, #e53e3e)', fontSize: '1.4rem' }}>*</span>
              </InputLabel>
              <TextInput
                id="doc-title"
                value={title}
                onChange={handleTitleChange}
                placeholder="Enter document title"
                $hasError={!!errors.title}
                disabled={buttonsDisabled}
                aria-describedby={errors.title ? 'title-error' : undefined}
              />
              {errors.title && (
                <ErrorText id="title-error" role="alert">
                  {errors.title}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="doc-description">
                Description{' '}
                <span style={{ color: 'var(--color-error, #e53e3e)', fontSize: '1.4rem' }}>*</span>
              </InputLabel>
              <TextAreaInput
                id="doc-description"
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Enter a short description of this document collection"
                rows={6}
                $hasError={!!errors.description}
                disabled={buttonsDisabled}
                aria-describedby={errors.description ? 'description-error' : undefined}
              />
              {errors.description && (
                <ErrorText id="description-error" role="alert">
                  {errors.description}
                </ErrorText>
              )}
              <CharacterCount>
                {description.length} / {MAX_DESCRIPTION_LENGTH}
              </CharacterCount>
            </FormGroup>
          </DocumentDetailsSection>

          <UploadSection>
            <SectionLabel>Upload Documents</SectionLabel>
            <DropZone onFilesSelect={handleFilesSelect} isUploading={isUploadingAny} />

            {errors.documents && (
              <ErrorText role="alert" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <AlertCircle size={13} />
                {errors.documents}
              </ErrorText>
            )}
          </UploadSection>
        </ContentGrid>

        <UploadedDocumentsSection>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem',
            }}
          >
            <SectionLabel style={{ margin: 0 }}>
              Uploaded Documents
              {documents.length > 0 && (
                <span
                  style={{ marginLeft: 8, fontWeight: 400, color: 'var(--color-text-tertiary)' }}
                >
                  ({documents.length})
                </span>
              )}
            </SectionLabel>
          </div>

          {documents.length === 0 ? (
            <EmptyState>
              <FileX size={32} strokeWidth={1.2} style={{ color: 'var(--color-text-tertiary)' }} />
              <p style={{ margin: 0 }}>No documents uploaded yet</p>
              <p style={{ margin: 0, fontSize: '12px' }}>Upload files using the drop zone above</p>
            </EmptyState>
          ) : (
            <DocumentsList role="list" aria-label="Uploaded documents">
              {documents.map((doc) => (
                <DocumentListItem
                  key={doc.id}
                  doc={doc}
                  onReupload={handleReupload}
                  onSkip={handleSkip}
                  disabled={buttonsDisabled}
                />
              ))}
            </DocumentsList>
          )}
        </UploadedDocumentsSection>
      </UploadDocumentsContainer>
    </UploadDocumentsWrapper>
  );
};

export default UploadDocuments;
